/**
 * Rain overlay easter egg
 *
 * Spawns a fullscreen overlay of falling images or emoji that self-cleans up.
 * Used by the Daybreak click trigger (favicon rain) and the seasonal click
 * trigger (snowflakes / hearts / pumpkins / etc. depending on the month).
 */

export type RainItem =
  | { kind: 'image'; src: string }
  | { kind: 'emoji'; char: string };

interface RainOptions {
  /** Number of drops to spawn. Default 70. */
  count?: number;
  /** Window in ms over which drops are staggered. Default 1400. */
  spawnWindowMs?: number;
  /** Min/max drop size in px (font-size for emoji, width/height for images). Default [18, 44]. */
  sizeRange?: [number, number];
  /** Min/max fall duration in seconds. Default [3.6, 7]. */
  durationRange?: [number, number];
}

const KEYFRAMES_ID = 'vera-rain-keyframes';

function ensureKeyframes() {
  if (document.getElementById(KEYFRAMES_ID)) return;
  const style = document.createElement('style');
  style.id = KEYFRAMES_ID;
  style.textContent = `
    @keyframes vera-rain-fall {
      0%   { transform: translate3d(0, -10vh, 0) rotate(var(--rot-start, 0deg)); opacity: 0; }
      8%   { opacity: 1; }
      90%  { opacity: 1; }
      100% { transform: translate3d(var(--drift, 0px), 110vh, 0) rotate(var(--rot-end, 360deg)); opacity: 0.85; }
    }
  `;
  document.head.appendChild(style);
}

export function rainOverlay(item: RainItem, opts: RainOptions = {}): void {
  ensureKeyframes();
  const {
    count = 70,
    spawnWindowMs = 1400,
    sizeRange = [18, 44],
    durationRange = [3.6, 7],
  } = opts;

  const container = document.createElement('div');
  container.style.cssText = [
    'position:fixed',
    'inset:0',
    'pointer-events:none',
    'z-index:100002',
    'overflow:hidden',
  ].join(';');
  document.body.appendChild(container);

  let remaining = count;
  const [minSize, maxSize] = sizeRange;
  const [minDur, maxDur] = durationRange;

  for (let i = 0; i < count; i++) {
    const delay = Math.random() * spawnWindowMs;
    window.setTimeout(() => {
      const drop = item.kind === 'image' ? document.createElement('img') : document.createElement('div');
      const size = minSize + Math.random() * (maxSize - minSize);
      const duration = minDur + Math.random() * (maxDur - minDur);
      const startX = Math.random() * 100;
      const drift = (Math.random() - 0.5) * 240;
      const rotStart = Math.random() * 360;
      const rotEnd = rotStart + (Math.random() > 0.5 ? 1 : -1) * (180 + Math.random() * 540);

      if (item.kind === 'image') {
        (drop as HTMLImageElement).src = item.src;
        (drop as HTMLImageElement).alt = '';
        drop.style.cssText = [
          'position:absolute',
          'top:0',
          `left:${startX}vw`,
          `width:${size}px`,
          `height:${size}px`,
          `--drift:${drift}px`,
          `--rot-start:${rotStart}deg`,
          `--rot-end:${rotEnd}deg`,
          `animation:vera-rain-fall ${duration}s linear forwards`,
          'will-change:transform,opacity',
          'filter:drop-shadow(0 2px 4px rgba(0,0,0,0.18))',
        ].join(';');
      } else {
        drop.textContent = item.char;
        drop.style.cssText = [
          'position:absolute',
          'top:0',
          `left:${startX}vw`,
          `font-size:${size}px`,
          'line-height:1',
          `--drift:${drift}px`,
          `--rot-start:${rotStart}deg`,
          `--rot-end:${rotEnd}deg`,
          `animation:vera-rain-fall ${duration}s linear forwards`,
          'will-change:transform,opacity',
          'user-select:none',
        ].join(';');
      }

      drop.addEventListener('animationend', () => {
        drop.remove();
        remaining--;
        if (remaining === 0) container.remove();
      });
      container.appendChild(drop);
    }, delay);
  }

  // Safety net — if any drops never fire animationend (tab backgrounded etc.),
  // tear the container down after the worst-case lifetime.
  window.setTimeout(() => container.remove(), spawnWindowMs + (maxDur * 1000) + 1000);
}

// =============================================================================
// Seasonal effect selector
// =============================================================================

export interface SeasonalEffect {
  /** Month (1-12) this effect is for — purely informational, used for labels. */
  month: number;
  /** Display name shown in tooltips. */
  label: string;
  /** Emoji used as the rain particle. */
  emoji: string;
}

const SEASONAL_EFFECTS: Record<number, SeasonalEffect> = {
  1:  { month: 1,  label: 'Winter snow',     emoji: '❄️' },
  2:  { month: 2,  label: 'Valentine hearts', emoji: '❤️' },
  3:  { month: 3,  label: 'Lucky clovers',    emoji: '🍀' },
  4:  { month: 4,  label: 'Cherry blossoms',  emoji: '🌸' },
  5:  { month: 5,  label: 'Spring tulips',    emoji: '🌷' },
  6:  { month: 6,  label: 'Sunshine',         emoji: '☀️' },
  7:  { month: 7,  label: 'Fireworks',        emoji: '🎆' },
  8:  { month: 8,  label: 'Sunflowers',       emoji: '🌻' },
  9:  { month: 9,  label: 'Autumn leaves',    emoji: '🍁' },
  10: { month: 10, label: 'Pumpkins',         emoji: '🎃' },
  11: { month: 11, label: 'Fall leaves',      emoji: '🍂' },
  12: { month: 12, label: 'Snowfall',         emoji: '❄️' },
};

export function getSeasonalEffect(date: Date = new Date()): SeasonalEffect {
  const month = date.getMonth() + 1;
  return SEASONAL_EFFECTS[month] ?? SEASONAL_EFFECTS[1]!;
}

// =============================================================================
// Run-across overlay (e.g. dinosaur stomp)
// =============================================================================

export type RunItem =
  | { kind: 'image'; src: string }
  | { kind: 'emoji'; char: string };

interface RunAcrossOptions {
  /** Size in px (font-size for emoji, width/height for images). Default 140. */
  size?: number;
  /** Slide duration in seconds for each instance. Default 4.5. */
  duration?: number;
  /** Direction of travel. Default 'rtl' (right→left; the 🦖 glyph faces left). */
  direction?: 'rtl' | 'ltr';
  /** Distance from the bottom of the viewport, in vh. Default 4. */
  bottomVh?: number;
  /** Number of instances to spawn. Default 1. */
  count?: number;
  /** Delay between successive spawns, in ms. Default 300. */
  staggerMs?: number;
}

const RUN_KEYFRAMES_ID = 'vera-run-across-keyframes';

function ensureRunKeyframes() {
  if (document.getElementById(RUN_KEYFRAMES_ID)) return;
  const style = document.createElement('style');
  style.id = RUN_KEYFRAMES_ID;
  style.textContent = `
    @keyframes vera-run-across-rtl {
      from { transform: translate3d(110vw, 0, 0); }
      to   { transform: translate3d(-30vw, 0, 0); }
    }
    @keyframes vera-run-across-ltr {
      from { transform: translate3d(-30vw, 0, 0) scaleX(-1); }
      to   { transform: translate3d(110vw, 0, 0) scaleX(-1); }
    }
    @keyframes vera-run-bounce {
      from { margin-bottom: 0; }
      to   { margin-bottom: 18px; }
    }
  `;
  document.head.appendChild(style);
}

export function runAcross(item: RunItem, opts: RunAcrossOptions = {}): void {
  ensureRunKeyframes();
  const {
    size = 140,
    duration = 4.5,
    direction = 'rtl',
    bottomVh = 4,
    count = 1,
    staggerMs = 300,
  } = opts;

  const spawnOne = () => {
    const wrap = document.createElement('div');
    wrap.style.cssText = [
      'position:fixed',
      `bottom:${bottomVh}vh`,
      'left:0',
      'pointer-events:none',
      'z-index:100002',
      'will-change:transform',
      `animation:vera-run-across-${direction} ${duration}s linear forwards`,
    ].join(';');

    let inner: HTMLElement;
    if (item.kind === 'emoji') {
      inner = document.createElement('div');
      inner.textContent = item.char;
      inner.style.cssText = [
        `font-size:${size}px`,
        'line-height:1',
        'user-select:none',
        'will-change:margin-bottom',
        'animation:vera-run-bounce 0.42s ease-in-out infinite alternate',
        'filter:drop-shadow(0 6px 8px rgba(0,0,0,0.35))',
      ].join(';');
    } else {
      inner = document.createElement('img');
      (inner as HTMLImageElement).src = item.src;
      (inner as HTMLImageElement).alt = '';
      inner.style.cssText = [
        `width:${size}px`,
        `height:${size}px`,
        'object-fit:contain',
        'display:block',
        'user-select:none',
        'will-change:margin-bottom',
        'animation:vera-run-bounce 0.42s ease-in-out infinite alternate',
        'filter:drop-shadow(0 6px 8px rgba(0,0,0,0.35))',
      ].join(';');
    }

    wrap.appendChild(inner);
    document.body.appendChild(wrap);

    wrap.addEventListener('animationend', () => wrap.remove());
    // Safety net in case animationend never fires.
    window.setTimeout(() => wrap.remove(), duration * 1000 + 1500);
  };

  for (let i = 0; i < count; i++) {
    if (i === 0) spawnOne();
    else window.setTimeout(spawnOne, i * staggerMs);
  }
}
